import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { token } = useAuth();
  const [dataStatus, setDataStatus] = useState(null); // { systemName, accountsCount, periodsCount, loadedAt }
  const [isLoading, setIsLoading] = useState(false);
  const [helperTeamsVersion, setHelperTeamsVersion] = useState(0); // Incremented when helper teams config changes

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      // Clear data status when token is removed (logout)
      setDataStatus(null);
    }
  }, [token]);

  // Check if data is loaded on mount
  const checkDataStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/data/status`);
      if (response.data.loaded) {
        setDataStatus(response.data);
      } else {
        setDataStatus(null);
      }
    } catch (error) {
      console.error('Error checking data status:', error);
      setDataStatus(null);
    }
  }, [token]);

  useEffect(() => {
    checkDataStatus();
  }, [checkDataStatus]);

  // Signal that helper teams configuration has changed
  const notifyHelperTeamsChanged = useCallback(() => {
    setHelperTeamsVersion(v => v + 1);
  }, []);

  // Upload data files
  const uploadData = async (accountsFile, periodsFile, systemName) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('accounts_file', accountsFile);
      formData.append('periods_file', periodsFile);
      formData.append('system_name', systemName);

      const response = await axios.post(`${API}/data/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setDataStatus(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error uploading data:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Fehler beim Hochladen der Daten' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Remove all data
  const removeData = async () => {
    setIsLoading(true);
    try {
      await axios.delete(`${API}/data/remove`);
      setDataStatus(null);
      return { success: true };
    } catch (error) {
      console.error('Error removing data:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Fehler beim Entfernen der Daten' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    dataStatus,
    isLoading,
    uploadData,
    removeData,
    checkDataStatus,
    isDataLoaded: !!dataStatus?.loaded,
    helperTeamsVersion,
    notifyHelperTeamsChanged
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
